import React, { memo, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { setupPickUpPointStrongSignInOperation } from '../../helpers/auth';
import { useOrder } from '../../hooks/useOrder';
import { useOrderId } from '../../hooks/useOrderId';
import { useCalculatedOrderStatus } from '../../hooks/validations/useCalculatedOrderStatus';
import { useLanguage } from '../../locale/useLanguage';
import { CalculatedOrderStatus, OrderState } from '../../models';
import { useAppDispatch } from '../../redux/core';
import { Routes } from '../../routes/types';
import { HomeButton } from '../HomeButton/HomeButton';
import {
  Assistence,
  DoormanIcon,
  EventIcon,
  ReviewIcon,
  SignIcon,
  WithdrawPointIcon,
} from '../Icons';
import { ModalRef } from '../Modal/Modal';
import { EditAddressWarningModal } from './components/EditAddressWarningModal/EditAddressWarningModal';

type HomeButtonBaseProps = {
  onClick?: () => void;
  isDisabled?: boolean;
};

export const WhenComeBackButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();

    const calculatedStatus = useCalculatedOrderStatus();
    const modalRef = useRef<ModalRef>(null);

    const navigateToEditDeliveryTime = useCallback(() => {
      history.push(Routes.ChooseDeliveryTime);
    }, [history]);

    const navigateToChoosePickUpPoint = useCallback(() => {
      history.push(Routes.SelectPickUpPoint);
    }, [history]);

    const navigateOrOpenModal = useCallback(() => {
      if (calculatedStatus === CalculatedOrderStatus.InTransit) {
        modalRef.current?.openModal();
      } else {
        navigateToEditDeliveryTime();
      }
    }, [calculatedStatus, navigateToEditDeliveryTime]);

    return (
      <>
        <EditAddressWarningModal
          ref={modalRef}
          onChoosePickUpPoint={navigateToChoosePickUpPoint}
          onEditAddress={navigateToEditDeliveryTime}
        />
        <HomeButton
          icon={<EventIcon width={'26px'} height={'26px'} />}
          label={
            calculatedStatus === CalculatedOrderStatus.ToReschedule
              ? t('homeButton.whenComeBackRescheduleButtonTitle')
              : t('homeButton.whenComeBackButtonTitle')
          }
          onClick={navigateOrOpenModal}
          isDisabled={isDisabled}
        />
      </>
    );
  }
);

export const ChooseWithdrawPointButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const navigateToSelectPickUpPoint = useCallback(() => {
      setupPickUpPointStrongSignInOperation(dispatch);
      history.push(Routes.SelectPickUpPoint);
    }, [dispatch, history]);

    return (
      <HomeButton
        icon={<WithdrawPointIcon width={'26px'} height={'26px'} />}
        label={t('homeButton.chooseWithdrawPointButtonTitle')}
        onClick={navigateToSelectPickUpPoint}
        isDisabled={isDisabled}
      />
    );
  }
);

export const ReceptionButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();

    const modalRef = useRef<ModalRef>(null);
    const calculatedStatus = useCalculatedOrderStatus();

    const navigateToEditDeliveryTime = useCallback(() => {
      history.push(Routes.OpeningHours);
    }, [history]);

    const navigateToChoosePickUpPoint = useCallback(() => {
      history.push(Routes.SelectPickUpPoint);
    }, [history]);

    const goToOpeningHours = useCallback(() => {
      if (calculatedStatus === CalculatedOrderStatus.InTransit) {
        modalRef.current?.openModal();
      } else {
        history.push(Routes.OpeningHours);
      }
    }, [calculatedStatus, history]);

    return (
      <>
        <EditAddressWarningModal
          ref={modalRef}
          onChoosePickUpPoint={navigateToChoosePickUpPoint}
          onEditAddress={navigateToEditDeliveryTime}
        />
        <HomeButton
          icon={<DoormanIcon width={'26px'} height={'26px'} />}
          label={t('homeButton.receptionButtonTitle')}
          onClick={goToOpeningHours}
          isDisabled={isDisabled}
        />
      </>
    );
  }
);

export const EditReceptionButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();

    const goToOpeningHours = useCallback(() => {
      history.push(Routes.OpeningHours);
    }, [history]);

    return (
      <HomeButton
        icon={<DoormanIcon width={'26px'} height={'26px'} />}
        label={t('homeButton.editReceptionButtonTitle')}
        onClick={goToOpeningHours}
        isDisabled={isDisabled}
      />
    );
  }
);

export const AssistenceButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();
    const orderId = useOrderId(true);

    const goToHelp = useCallback(() => {
      history.push(Routes.Help.replace(':mlkId', `${orderId}`));
    }, [history, orderId]);

    return (
      <HomeButton
        icon={<Assistence width={26} height={26} />}
        label={t('homeButton.assistenceButtonTitle')}
        onClick={goToHelp}
        isDisabled={isDisabled}
      />
    );
  }
);

export const EditAddressButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();
    const calculatedStatus = useCalculatedOrderStatus();
    const order = useOrder()
    const modalRef = useRef<ModalRef>(null);

    const navigateToChangeAddress = useCallback(() => {
      history.push(Routes.ChangeAddress);
    }, [history]);

    const navigateToChoosePickUpPoint = useCallback(() => {
      history.push(Routes.SelectPickUpPoint);
    }, [history]);

    const navigateOrOpenModal = useCallback(() => {
      // HERE: add baseline_ready
      if (calculatedStatus === CalculatedOrderStatus.InTransit || order?.status === OrderState.Baseline) {
        modalRef.current?.openModal();
      } else {
        navigateToChangeAddress();
      }
    }, [order, calculatedStatus, navigateToChangeAddress]);

    return (
      <>
        <EditAddressWarningModal
          ref={modalRef}
          onChoosePickUpPoint={navigateToChoosePickUpPoint}
          onEditAddress={navigateToChangeAddress}
        />
        <HomeButton
          icon={<SignIcon width={'26px'} height={'26px'} />}
          label={t('homeButton.editAddressButtonTitle')}
          onClick={navigateOrOpenModal}
          isDisabled={isDisabled}
        />
      </>
    );
  }
);

export const ReviewIntercomButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();

    const navigateToChangeAddress = useCallback(() => {
      history.push(Routes.ChangeAddress);
    }, [history]);

    return (
      <HomeButton
        icon={<SignIcon width={'26px'} height={'26px'} />}
        label={t('homeButton.reviewIntercomButtonTitle')}
        onClick={navigateToChangeAddress}
        isDisabled={isDisabled}
      />
    );
  }
);

export const ReviewAddressButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();

    const navigateToChangeAddress = useCallback(() => {
      history.push(Routes.ChangeAddress);
    }, [history]);

    return (
      <HomeButton
        icon={<SignIcon width={'26px'} height={'26px'} />}
        label={t('homeButton.reviewAddressButtonTitle')}
        onClick={navigateToChangeAddress}
        isDisabled={isDisabled}
      />
    );
  }
);

export const ReviewButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();
    const history = useHistory();

    const navigateToReview = useCallback(() => {
      history.push(Routes.Review);
    }, [history]);

    return (
      <HomeButton
        icon={<ReviewIcon />}
        label={t('homeButton.reviewServiceButtonTitle')}
        onClick={navigateToReview}
        isDisabled={isDisabled}
      />
    );
  }
);

export const EditReviewButton: React.FC<HomeButtonBaseProps> = memo(
  ({ isDisabled }) => {
    const [t] = useLanguage();

    const history = useHistory();

    const navigateToReview = useCallback(() => {
      history.push(Routes.Review);
    }, [history]);

    return (
      <HomeButton
        icon={<ReviewIcon />}
        label={t('homeButton.editReviewButtonTitle')}
        onClick={navigateToReview}
        isDisabled={isDisabled}
      />
    );
  }
);
